import { useTranslation, type TFunction } from 'react-i18next';
import type { LangOption } from './types';

export type UseLocalesResult<T extends LangOption> = {
  translate: TFunction;
  currentLang: T;
  allLangs: readonly T[];
  onChangeLang: (newLang: string) => Promise<void>;
};

export type UseLocalesHook<T extends LangOption> = () => UseLocalesResult<T>;

export type CreateUseLocalesOptions<T extends LangOption> = {
  allLangs: readonly T[];
  defaultLang: T;
};

/**
 * Build the `useLocales` hook bound to an app's language list.
 *
 * Each app calls this once with its own `allLangs` / `defaultLang` and
 * exports the returned hook. Components consume it the same way mobile
 * has always done — `const { translate, currentLang, ... } = useLocales()`.
 *
 * `currentLang` is derived from `i18n.language` (reactive — re-renders on
 * language change via react-i18next's context) rather than localStorage,
 * so it works on the server and re-renders predictably on client.
 */
export function createUseLocales<T extends LangOption>({
  allLangs,
  defaultLang,
}: CreateUseLocalesOptions<T>): UseLocalesHook<T> {
  return function useLocales() {
    const { i18n, t: translate } = useTranslation();

    const currentLang = allLangs.find((lang) => lang.value === i18n.language) ?? defaultLang;

    const onChangeLang = async (newLang: string) => {
      await i18n.changeLanguage(newLang);
    };

    return {
      translate,
      currentLang,
      allLangs,
      onChangeLang,
    };
  };
}
